import { PeriodicityEnum } from './funnel-bien-locatif.service';

/**
 * Types partagés entre tous les services immobiliers
 *
 * Ce fichier centralise les types/interfaces/enums utilisés par plusieurs services :
 * - residence-principale.service.ts
 * - bien-locatif.service.ts
 * - bien-jouissance.service.ts
 * - scpi.service.ts
 * - bien-immo-orchestrator.service.ts
 */

/**
 * Type de bien immobilier (catégorie principale)
 * Utilisé pour la classification des biens dans le système
 */
export enum EstateTypeEnum {
  RESIDENCE_PRINCIPALE = 'RESIDENCE_PRINCIPALE',
  ENJOYMENT = 'ENJOYMENT',
  RENTAL = 'RENTAL',
  SCPI = 'SCPI',
}

/**
 * Type de produit
 */
export enum ProductTypeEnum {
  PERSO_USE = 'PERSO_USE',
  UNFURNISHED = 'UNFURNISHED',
  LMNP = 'LMNP',
  SCPI = 'SCPI',
}

/**
 * Type de bien de jouissance
 *
 * Note: Les valeurs RESIDENCE_SECOND_HOUSE et RESIDENCE_SECOND_APARTMENT
 * sont des extensions frontend pour l'UX. Le backend n'a que RESIDENCE_SECOND.
 */
export enum PersonalUseTypeEnum {
  RESIDENCE_PRINCIP = 'RESIDENCE_PRINCIP', // Résidence principale
  RESIDENCE_SECOND = 'RESIDENCE_SECOND', // Résidence secondaire
  RESIDENCE_SECOND_HOUSE = 'RESIDENCE_SECOND_HOUSE', // Résidence secondaire (maison) - UI only
  RESIDENCE_SECOND_APARTMENT = 'RESIDENCE_SECOND_APARTMENT', // Résidence secondaire (appartement) - UI only
  SURFACE = 'SURFACE',
  WOODS = 'WOODS',
  GARAGE = 'GARAGE',
  LAND = 'LAND',
}

/**
 * Type de propriété
 */
export enum PropertyTypeEnum {
  HOUSE = 'HOUSE',
  APARTMENT = 'APPARTMENT',
  PROFESSIONNAL = 'PROFESSIONNAL',
}

/**
 * Type de dépense
 */
export enum ExpenseTypeEnum {
  NOTARY = 'NOTARY',
  AGENCY = 'AGENCY',
  CONDO_FEE = 'CONDO_FEE',
  AGENCY_HANDLING = 'AGENCY_HANDLING',
  INSURANCE = 'INSURANCE',
  PROPERTY_TAX = 'PROPERTY_TAX',
  WORK = 'WORK',
  RENT = 'RENT',
  HOUSING_TAX = 'HOUSING_TAX',
  CFE = 'CFE',
  ACCOUNTING_FEES = 'ACCOUNTING_FEES',
}

/**
 * Charges gérées par le funnel pour un bien locatif : supprimées puis recréées
 * depuis le formulaire à chaque enregistrement (taxe d'habitation et CFE ne
 * sont recréées qu'en meublé). Les autres charges du bien (notaire, agence,
 * travaux…) sont saisies par le pro et ne sont pas touchées par le funnel.
 */
export const FUNNEL_RENTAL_EXPENSE_TYPES: ReadonlyArray<ExpenseTypeEnum> = [
  ExpenseTypeEnum.PROPERTY_TAX,
  ExpenseTypeEnum.CONDO_FEE,
  ExpenseTypeEnum.AGENCY_HANDLING,
  ExpenseTypeEnum.INSURANCE,
  ExpenseTypeEnum.ACCOUNTING_FEES,
  ExpenseTypeEnum.HOUSING_TAX,
  ExpenseTypeEnum.CFE,
];

/**
 * Informations sur la propriété physique
 */
export interface PropertyDto {
  id?: number;
  areaSize: number;
  areaSizePrice?: number;
  areaSizeEstimatedPrice?: number;
  type: PropertyTypeEnum;
  label?: string;
  street?: string;
  street2?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  countryTrad?: string;
}

/**
 * Dépense liée au bien
 */
export interface ExpenseDto {
  id?: number;
  label: string;
  amount: number | string;
  date: string;
  type: ExpenseTypeEnum;
  frequency?: PeriodicityEnum.MENS | PeriodicityEnum.ANNU | PeriodicityEnum.ONE;
}

/**
 * Informations d'investissement (champs communs)
 *
 * Interface de base pour tous les types d'investissement immobilier :
 * - PersonalUsePropertyDto (résidence principale, secondaire, etc.)
 * - UnfurnishedDto, LmnpLmpDto (biens locatifs)
 * - ScpiDto (SCPI)
 */
export interface InvestmentDto {
  accountId: number;
  subscriptionSource?: string;
  subscriptionSourceDetail?: string;
  investmentType?: string;
  startDate: string;
  endDate?: string;
  purchasePrice: number;
  sellPrice?: number;
  lastValue: number;
  current: boolean;
  owners?: Array<{ investmentId?: number; ownerId: number; share?: number }>;
}
